import {
	parseDateString, formatDate, DATE_FORMATS, addDays, todayMidnight
} from './dateUtils';
import { downloadFile } from './downloadFile';
import { buildCSVString } from './csvUtils';
import {
	TTransportShipment, CHIP_TYPE,
	ShipmentLocation, MatchPriority, CarrierMatch, CarrierResult, EfficiencyOpportunity,
} from '@types';

export type {
	ShipmentLocation, MatchPriority, CarrierMatch, CarrierResult, EfficiencyOpportunity
};

const TEMP_MAP: Record<number, string> = {
	1: 'AMBIENT',
	2: 'CHILLED',
	3: 'FROZEN',
};

export const locLabel = (loc: ShipmentLocation | null | undefined): string => {
	if (!loc) return '—';
	return loc.address || [loc.suburb, loc.state, loc.postcode].filter(Boolean).join(', ') || '—';
};

export const locPostcode = (loc: ShipmentLocation | null | undefined): string | null => {
	if (!loc) return null;
	if (loc.postcode) return String(loc.postcode);
	const addr = loc.address || '';
	const m = addr.match(/\b(\d{4})\b/);
	return m ? m[1] : null;
};

export const locState = (loc: ShipmentLocation | null | undefined): string | null => {
	if (!loc) return null;
	if (loc.state) return loc.state.toUpperCase();
	const addr = loc.address || '';
	const m = addr.match(/\b(NSW|VIC|QLD|SA|WA|TAS|NT|ACT)\b/i);
	return m ? m[1].toUpperCase() : null;
};

export const matchPriority = (
	dropoffLoc: ShipmentLocation | null | undefined,
	pickupLoc: ShipmentLocation | null | undefined,
): MatchPriority | null => {
	const dp = locPostcode(dropoffLoc);
	const pp = locPostcode(pickupLoc);
	const ds = locState(dropoffLoc);
	const ps = locState(pickupLoc);

	// P1 — suburb exact match
	if (dropoffLoc?.suburb && pickupLoc?.suburb &&
		dropoffLoc.suburb.toLowerCase() === pickupLoc.suburb.toLowerCase()) {
		return {
			level: 1, label: 'P1 - suburb match', chipType: CHIP_TYPE.SUCCESS
		};
	}

	if (dp && pp) {
		// P2 — exact postcode match
		if (dp === pp) {
			return {
				level: 2, label: 'P2 - postcode exact', chipType: CHIP_TYPE.SUCCESS
			};
		}
		// P3 — first 3 digits of postcode match
		if (dp[0] === pp[0] && dp[1] === pp[1] && dp[2] === pp[2]) {
			return {
				level: 3, label: 'P3 - postcode 3-digit', chipType: CHIP_TYPE.INFO
			};
		}
		// P4 — first 2 digits of postcode match
		if (dp[0] === pp[0] && dp[1] === pp[1]) {
			return {
				level: 4, label: 'P4 - postcode 2-digit', chipType: CHIP_TYPE.WARN
			};
		}
	}

	// P5 — state match only
	if (ds && ps && ds === ps) {
		return {
			level: 5, label: 'P5 - state match', chipType: CHIP_TYPE.NEUTRAL
		};
	}

	return null;
};

export const getCarrier = (s: TTransportShipment): string | null => {
	const flat = s as any;
	return s.manifest?.master_manifest?.carrier?.company?.business_name
		|| flat['manifest.master_manifest.carrier.company.business_name']
		|| s.superdock_carriers?.[0]?.carrier_name
		|| null;
};

export const getShipperDisplay = (s: TTransportShipment): string | null => {
	const flat = s as any;
	return s.master_shipment?.shipper?.cost_centre_name
		|| flat['master_shipment.shipper.cost_centre_name']
		|| s.master_shipment?.shipper?.company?.business_name
		|| flat['master_shipment.shipper.company.business_name']
		|| flat.shipper?.cost_centre_name
		|| flat.shipper?.cost_center_name
		|| flat.shipper?.company?.business_name
		|| null;
};

export const getShipperSearchIndex = (s: TTransportShipment): string => {
	const flat = s as any;
	const parent = (
		s.master_shipment?.shipper?.company?.business_name
		|| flat['master_shipment.shipper.company.business_name']
		|| flat.shipper?.company?.business_name
		|| ''
	).toLowerCase();
	const child = (
		s.master_shipment?.shipper?.cost_centre_name
		|| flat['master_shipment.shipper.cost_centre_name']
		|| flat.shipper?.cost_centre_name
		|| flat.shipper?.cost_center_name
		|| ''
	).toLowerCase();
	return [parent, child].filter(Boolean).join(' ');
};

export const getPickupLoc = (s: TTransportShipment): ShipmentLocation | null => {
	const [c] = (s.cargos || []);
	return (c && c.pickup) ? c.pickup : null;
};

export const getDropoffLoc = (s: TTransportShipment): ShipmentLocation | null => {
	const [c] = (s.cargos || []);
	return (c && c.dropoff) ? c.dropoff : null;
};

export const getTemp = (s: TTransportShipment): string => {
	const t = s.temperature as unknown;
	if (!t) return '';
	if (typeof t === 'number') return TEMP_MAP[t] || String(t);
	const str = String(t).trim();
	if (isNaN(Number(str))) return str.toUpperCase();
	return TEMP_MAP[parseInt(str)] || str.toUpperCase();
};

export const getShipmentTruckType = (s: TTransportShipment): string => {
	const t = s.req_truck_type;
	if (!t) return 'Any';
	if (typeof t === 'object' && (t as any).name) return String((t as any).name).toUpperCase();
	return String(t).trim().toUpperCase() || 'Any';
};

export const getFreight = (s: TTransportShipment): string => {
	return (s.freight_mode || '').toUpperCase().trim();
};

export const getPallets = (s: TTransportShipment): number => {
	const [c] = (s.cargos || []);
	return parseInt(String(c?.pallet_qty ?? 0)) || 0;
};

export const getSpaces = (s: TTransportShipment): number => {
	const [c] = (s.cargos || []);
	return parseInt(String(c?.qty ?? 0)) || 0;
};

export const getWeight = (s: TTransportShipment): number => {
	const [c] = (s.cargos || []);
	return parseFloat(String(c?.total_weight ?? c?.weight ?? 0)) || 0;
};

export const getCargo = (s: TTransportShipment): string => {
	return s.cargo_type || s.freight_mode || '—';
};

export const getRef = (s: TTransportShipment): string => {
	return s.reference || s.id || '—';
};

const parseShipmentDate = (raw: string | number | null | undefined): Date | null => {
	if (!raw && raw !== 0) return null;
	if (typeof raw === 'number') {
		const str = String(Math.floor(raw));
		if (str.length === 8) {
			const d = new Date(+str.slice(0, 4), +str.slice(4, 6) - 1, +str.slice(6, 8));
			return isNaN(d.getTime()) ? null : d;
		}
		const ms = str.length <= 10 ? raw * 1000 : raw;
		const d = new Date(ms);
		return isNaN(d.getTime()) ? null : d;
	}
	if (typeof raw === 'string') {
		if (raw.match(/^\d{4}-\d{2}-\d{2}/)) {
			const p = raw.slice(0, 10).split('-');
			return new Date(+p[0], +p[1] - 1, +p[2]);
		}
		const d = parseDateString(raw, 'ISO');
		return isNaN(d.getTime()) ? null : d;
	}
	return null;
};

export const getPickupDate = (s: TTransportShipment): Date | null => {
	const [c] = (s.cargos || []);
	return parseShipmentDate(s.revised_pickup?.request_arrival_date || (c && c.pickup_at) || s.original_pickup_on);
};

export const getDropoffDate = (s: TTransportShipment): Date | null => {
	const [c] = (s.cargos || []);
	return parseShipmentDate(s.revised_dropoff?.request_arrival_date || (c && c.drop_at) || s.original_dropoff_on);
};

export const toDateStr = (d: Date | null | undefined): string => {
	if (!d) return '—';
	return formatDate(d, DATE_FORMATS.DATE_WITH_SHORT_MONTH_NAME);
};

export const runCarrierRec = (
	unallocated: TTransportShipment[],
	allocated: TTransportShipment[],
): CarrierResult[] => {
	const withCarrier = allocated.filter((s) => getCarrier(s));
	const results: CarrierResult[] = [];

	for (const u of unallocated) {
		const uPickup = getPickupLoc(u);
		const uTemp = getTemp(u);
		const uTruck = getShipmentTruckType(u);
		const uFreight = getFreight(u);
		const shipper = getShipperDisplay(u) || '—';
		const shipperSearchIndex = getShipperSearchIndex(u);
		const matches: CarrierMatch[] = [];

		const uPickupDate = getPickupDate(u);
		const uPickupDay = uPickupDate ? new Date(uPickupDate).setHours(0, 0, 0, 0) : null;

		for (const a of withCarrier) {
			if (uPickupDay !== null) {
				const aDropoffDate = getDropoffDate(a);
				if (!aDropoffDate) continue;
				const aDropoffDay = new Date(aDropoffDate).setHours(0, 0, 0, 0);
				const dayDiff = (uPickupDay - aDropoffDay) / 86400000;
				if (dayDiff < 0 || dayDiff > 1) continue;
			}

			const aDropoff = getDropoffLoc(a);
			const priority = matchPriority(aDropoff, uPickup);
			if (!priority) continue;

			const aTemp = getTemp(a);
			const aTruck = getShipmentTruckType(a);
			const aFreight = getFreight(a);
			const tempOk = !uTemp || !aTemp || uTemp === aTemp;
			const truckOk = uTruck === 'Any' || aTruck === 'Any' || uTruck === aTruck;
			const freightOk = !uFreight || !aFreight || uFreight === aFreight;

			const flags: string[] = [];
			if (!tempOk) flags.push('⚠ Temp mismatch');
			if (!truckOk) flags.push('⚠ Truck mismatch');
			if (!freightOk) flags.push('⚠ Freight mismatch');

			matches.push({
				carrier: getCarrier(a),
				shipRef: getRef(a),
				dropoffLoc: aDropoff,
				dropoffDate: getDropoffDate(a),
				truckType: aTruck,
				temp: aTemp || '—',
				cargoType: getCargo(a),
				pallets: getPallets(a),
				spaces: getSpaces(a),
				priority,
				flags,
				allShipments: [],
			});
		}

		const uPickupMs = (getPickupDate(u) || new Date()).getTime();
		matches.sort((a, b) => {
			if (a.priority.level !== b.priority.level) return a.priority.level - b.priority.level;
			const da = a.dropoffDate ? Math.abs(a.dropoffDate.getTime() - uPickupMs) : Infinity;
			const db = b.dropoffDate ? Math.abs(b.dropoffDate.getTime() - uPickupMs) : Infinity;
			return da - db;
		});

		const carrierMap = new Map<string, CarrierMatch>();
		for (const m of matches) {
			const key = (m.carrier || '').toLowerCase();
			if (!carrierMap.has(key)) {
				carrierMap.set(key, { ...m, allShipments: [m] });
			} else {
				carrierMap.get(key)!.allShipments.push(m);
			}
		}
		const dedupedMatches = Array.from(carrierMap.values());

		results.push({
			shipment: u,
			ref: getRef(u),
			pickupLoc: uPickup,
			pickupDate: getPickupDate(u),
			temp: uTemp,
			truckType: uTruck,
			freight: uFreight,
			shipper,
			shipperSearchIndex,
			matches: dedupedMatches.slice(0, 10),
			rawMatches: matches,
		});
	}

	results.sort((a, b) => {
		const aLevel = a.matches.length > 0 ? a.matches[0].priority.level : Infinity;
		const bLevel = b.matches.length > 0 ? b.matches[0].priority.level : Infinity;
		return aLevel - bLevel;
	});

	return results;
};

export const runEfficiencyRec = (
	unallocated: TTransportShipment[],
): EfficiencyOpportunity[] => {
	const today = todayMidnight();
	const cutoff = addDays(today, 14);
	const TRUCKS = [
		{
			label: 'Single', pallets: 48, spaces: 24, maxKg: 24000
		},
		{
			label: 'B-Double', pallets: 72, spaces: 36, maxKg: 36000
		},
		{
			label: 'Super B-Double', pallets: 84, spaces: 42, maxKg: 42000
		},
		{
			label: 'B-Triple', pallets: 100, spaces: 50, maxKg: 50000
		},
	];
	const maxSpaces = Math.max(...TRUCKS.map((t) => t.spaces));
	const maxWeight = Math.max(...TRUCKS.map((t) => t.maxKg));

	const candidates = unallocated.filter((s) => {
		const cargo = getCargo(s).toLowerCase();
		if (!cargo.includes('less than') && !cargo.includes('ltl')) return false;
		const pd = getPickupDate(s);
		if (!pd || isNaN(pd.getTime())) return false;
		const d = new Date(pd);
		d.setHours(0, 0, 0, 0);
		return d >= today && d <= cutoff;
	});

	const processed = new Set<string>();
	const opportunities: EfficiencyOpportunity[] = [];

	for (let i = 0; i < candidates.length; i++) {
		const a = candidates[i];
		const aId = getRef(a);
		if (processed.has(aId)) continue;

		const aLoc = getPickupLoc(a);
		const aTemp = getTemp(a);
		const aDate = getPickupDate(a);
		const aDateMs = aDate ? new Date(aDate).setHours(0, 0, 0, 0) : null;

		const group: TTransportShipment[] = [a];
		let totalSpaces = getSpaces(a);
		let totalWeight = getWeight(a);

		for (let j = i + 1; j < candidates.length; j++) {
			const b = candidates[j];
			const bId = getRef(b);
			if (processed.has(bId)) continue;

			const bTemp = getTemp(b);
			if (aTemp && bTemp && aTemp !== bTemp) continue;

			const bLoc = getPickupLoc(b);
			const match = matchPriority(aLoc, bLoc) || matchPriority(bLoc, aLoc);
			if (!match) continue;

			const bDate = getPickupDate(b);
			const bDateMs = bDate ? new Date(bDate).setHours(0, 0, 0, 0) : null;
			if (aDateMs && bDateMs && Math.abs(aDateMs - bDateMs) > 7 * 86400000) continue;

			const newSpaces = totalSpaces + getSpaces(b);
			const newWeight = totalWeight + getWeight(b);
			if (newSpaces > maxSpaces || newWeight > maxWeight) continue;

			group.push(b);
			totalSpaces = newSpaces;
			totalWeight = newWeight;
			processed.add(bId);
		}

		if (group.length > 1) {
			processed.add(aId);
			const dates = group.map((s) => getPickupDate(s)).filter((d): d is Date => d !== null);
			const suggestedDate = dates.reduce((latest, d) => (d > latest ? d : latest), dates[0]) ?? null;
			opportunities.push({
				group,
				totalSpaces,
				totalWeight,
				temp: aTemp,
				location: locLabel(aLoc),
				suggestedDate,
			});
		}
	}

	return opportunities;
};

export const downloadCarrierCSV = (results: CarrierResult[], efficiencyRefs: Set<string>): void => {
	const headers = [
		'Shipment Ref', 'Pickup Location', 'Pickup Date', 'Temp', 'Truck Type', 'Freight', 'Shipper',
		'Efficiency Flag', 'Matched Carrier', 'Carrier Dropoff Location', 'Carrier Dropoff Date',
		'Carrier Shipment Ref', 'Priority Level', 'Priority Label', 'Flags',
	];
	const rows: Record<string, unknown>[] = [];
	for (const r of results) {
		if (r.matches.length === 0) {
			rows.push({
				'Shipment Ref': r.ref, 'Pickup Location': locLabel(r.pickupLoc),
				'Pickup Date': toDateStr(r.pickupDate), 'Temp': r.temp, 'Truck Type': r.truckType,
				'Freight': r.freight, 'Shipper': r.shipper,
				'Efficiency Flag': efficiencyRefs.has(r.ref) ? 'Yes' : '',
				'Matched Carrier': '', 'Carrier Dropoff Location': '', 'Carrier Dropoff Date': '',
				'Carrier Shipment Ref': '', 'Priority Level': '', 'Priority Label': '', 'Flags': '',
			});
		} else {
			for (const m of r.rawMatches) {
				rows.push({
					'Shipment Ref': r.ref, 'Pickup Location': locLabel(r.pickupLoc),
					'Pickup Date': toDateStr(r.pickupDate), 'Temp': r.temp, 'Truck Type': r.truckType,
					'Freight': r.freight, 'Shipper': r.shipper,
					'Efficiency Flag': efficiencyRefs.has(r.ref) ? 'Yes' : '',
					'Matched Carrier': m.carrier || '', 'Carrier Dropoff Location': locLabel(m.dropoffLoc),
					'Carrier Dropoff Date': toDateStr(m.dropoffDate), 'Carrier Shipment Ref': m.shipRef,
					'Priority Level': m.priority.level, 'Priority Label': m.priority.label,
					'Flags': m.flags.join('; '),
				});
			}
		}
	}
	downloadFile(buildCSVString(headers, rows), `carrier_recommendations_${new Date().toISOString().slice(0, 10)}.csv`);
};

export const downloadEfficiencyCSV = (results: EfficiencyOpportunity[]): void => {
	const headers = [
		'Opportunity', 'Shipment Ref', 'Pickup Location', 'Current Pickup Date', 'Suggested Pickup Date',
		'Date Change Required', 'Pallets', 'Pallet Spaces', 'Weight (kg)',
		'Temperature', 'Group Total Spaces', 'Group Total Weight (kg)',
	];
	const rows: Record<string, unknown>[] = [];
	results.forEach((opp, idx) => {
		opp.group.forEach((s) => {
			const pd = getPickupDate(s);
			const needsChange = pd && opp.suggestedDate && pd.toDateString() !== opp.suggestedDate.toDateString();
			rows.push({
				'Opportunity': idx + 1, 'Shipment Ref': getRef(s),
				'Pickup Location': locLabel(getPickupLoc(s)), 'Current Pickup Date': toDateStr(pd),
				'Suggested Pickup Date': toDateStr(opp.suggestedDate),
				'Date Change Required': needsChange ? 'Yes' : 'No',
				'Pallets': getPallets(s), 'Pallet Spaces': getSpaces(s), 'Weight (kg)': getWeight(s),
				'Temperature': opp.temp, 'Group Total Spaces': opp.totalSpaces,
				'Group Total Weight (kg)': opp.totalWeight,
			});
		});
	});
	downloadFile(buildCSVString(headers, rows), `efficiency_recommendations_${new Date().toISOString().slice(0, 10)}.csv`);
};
